import type { Kjede } from './types';

const mapToPromptFormat = (kjeder: Kjede[]): string => {
  let result = '';

  // Helper function to recursively build the formatted string with indentation
  const formatWithIndentation = (node: any, level: number = 0) => {
    // Add indentation based on hierarchy level
    const indent = '\t'.repeat(level);
    // Add the node name
    result += `${indent}${node.name} (${node.kjedenr})\n`;

    // Process children recursively with increased indentation
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => {
        formatWithIndentation(child, level + 1);
      });
    }
  };
  // Process each top-level node
  kjeder.forEach((node) => {
    formatWithIndentation(node);
  });

  return result;
};

export const createBasicInfoPrompt = (text: string) => {
  return `
	Extract the following information from the text:
	- GLN
	- Organisasjonsnummer
	- Telefonnummer (only extract if it contains actual digits, ignore text like 'ikke avklart', 'ukjent', etc.)
	- Besøksadresse
	- Nedlagtdato
	- Enhetnavn
	- Butikknummer
	- FirmaNavn
	- Besøk Postnummer
	- Besøk Poststed
	- Faktura Adresse
	- Faktura Postnummer
	- Faktura Poststed
	- Post Adresse
	- Postnummer
	- Post Poststed
	- Kommentar
	- Leverandør Kundenummer
	- Leverandør Kundenummer Hos
	- Kundenummer hos grossist (also referred to as "Kundenummer Distribunal" or similar distribution/wholesale customer numbers)
	- Bransje Forslag
	- Kontonummer (if in the format like 8450.45.45457, remove the periods to return as 84504545457)
	- Oppstart dato
	- Omsetning
	- Omsetningsårstall
	- Salgsareal (also referred to as Salgsflate)
	- Faktura Epost (if you dont find it keep the field empty, dont add Regnskapsfører Epost info to this field)
	- Butikk Epost
	- Medlem hos (Extract from labels like "Kjedetilhørighet: into the "Medlem hos filed. This represents the chain name the business belongs to. If no chain affiliation is found, leave empty)
	- Medlem hos Innmeldingsdato also referred as oppstartsdato

	IMPORTANT: 
	- When you see "Kundenummer Distribunal:" this should be mapped to "kundenummerhossgrossist" field
	- This is different from "Leverandør Kundenummer" fields


	The input text is: "${text}"

	Return the information in JSON format with the following keys:
	{
		"gln": "value",
		"organisasjonsnummer": "value",
		"telefonnummer": "value",
		"besoksadresse": "value",
		"nedlagtdato": "value",
		"enhetnavn": "value",
		"butikknummer": "value",
		"firmanavn": "value",
		"besokpostnummer": "value",
		"besokpoststed": "value",
		"fakturaadresse": "value",
		"fakturapostnummer": "value",
		"fakturapoststed": "value",
		"postadresse": "value",
		"postnummer": "value",
		"postpoststed": "value",
		"kommentar": "value",
		"leverandorkundenummer": "value",
		"leverandorkundenummerhos": "value",
		"kundenummerhossgrossist": "value",
		"bransjeforslag": "value",
		"kontonummer": "value",
		"oppstartsdato": "value",
		"omsetning": "value",
		"salgsareal": "value",
		"fakturaEpost": "value",
		"epost": "value"
		"MedlemHos": "value",
		"MedlemsNummer": "value",
		"MedlemFraOgMedDato": "value",
		"MedlemTilOgMedDato": "value"
	}`;
};

export const createMarkedskjedePrompt = (text: string, mappedMarkedKjeder: Kjede[]) => {
  return `
	///////////// MARKED CHAINS DETECTION /////////////
	- Markedskjede (Marked Chains) with hierarchical structure:
	${mapToPromptFormat(mappedMarkedKjeder)}

	Marked Chain Detection Guide:
	The following shows the hierarchical structure of marked chains. Each chain can have up to 3 levels:

	Level 1 (Main Chains):
	- Stand-alone chains (e.g., "BILTEMA", "ABILITY FM")
	- Parent chains with sub-chains

	Level 2 (Sub-chains):
	- Direct subsidiaries under main chains
	- Regional divisions

	Level 3 (Sub-sub-chains):
	- Store type (e.g., "REMA 1000 SUPERMARKED", "REMA 1000 NÆRBUTIKK")
	- Specific formats based on store size/type

	Marked Chain Matching Rules:
	1. Look for exact or partial matches in chain names
	2. Use these specific keywords that appear in emails across different chains:
	   - "Butikkstørrelse:" or "Butikktype:" or similar labels indicating store format/size 
		 (Look for values like "Liten", "Stor", "Mini", "Middels", "Vanlig", "XL")
	   - "Region:" indicating the regional classification
	   - "Salgsflate:" or "Salgsareal:" showing the store size in square meters
	   - "Butikknummer:" which can identify specific chain locations
	3. Apply a hierarchical detection approach:
	   - First, identify the parent chain in the text (e.g., "REMA 1000", "BUNNPRIS")
	   - Then look for specific format/size information:
		 * Match "Butikkstørrelse: Liten" with chains containing "LITEN"
		 * Match "Butikkstørrelse: Stor" with chains containing "STOR"
		 * Match "Butikkstørrelse: Vanlig" with chains containing "VANLIG"
	   - Use "Butikknavn:" field which often contains the full chain name with type
	   - The "Region:" field often indicates regional sub-chains
	4. For other cases Example:
	   - For REMA 1000, "Region: Region Nordre Østland" can help identify regional chain
	   - Look for specific regional divisions in different chains
	   - If you see "Butikkstørrelse: Liten" for a parent chain, search for child chains with "LITEN" in the name
	5. Match chains case-insensitively
	6. Use numeric values in combination with keywords:
	   - "Salgsflate: 712" combined with "Butikkstørrelse: Liten" helps confirm the correct sub-chain
	   - Map numeric ranges to size categories if butikkstørrelse is not explicitly mentioned

	The input text is: "${text}"
	   
	Based on the text, identify the most likely markedskjede (market chain).
	Return ONLY the name and number in the format "CHAIN_NAME (NUMBER)" or just null if no match found.`;
};

export const createRegionalkjedePrompt = (text: string, mappedRegionKjeder: Kjede[]) => {
  return `
	///////////// REGIONAL CHAINS DETECTION /////////////
	- Regionalkjeder (Regional Chains) with hierarchical structure:		
	${mapToPromptFormat(mappedRegionKjeder)}
	
	Regional Chain Detection Guide:
	The following shows the hierarchical structure of regional chains. Each chain can have up to 3 levels:

	Level 1 (Main Chains):
	- Stand-alone chains (e.g., "BILTEMA", "ABILITY FM")
	- Parent chains with sub-chains

	Level 2 (Sub-chains):
	- Direct subsidiaries (e.g., "4SERVICE CATERING" under "4SERVICE GRUPPEN AS")
	- Regional divisions (e.g., "BUNNPRIS REGION" under "BUNNPRIS")

	Level 3 (Sub-sub-chains):
	- Local units (e.g., "BUNNPRIS MØRE" under "BUNNPRIS REGION")
	- Geographical areas (e.g., "BUNNPRIS NORD", "BUNNPRIS SØR")

	Regional Chain Matching Rules:
	1. Examples:
	   - Input: "COMPASS PROFIL 1" → Return: "COMPASS GROUP"
	   - Input: "4SERVICE CATERING" → Return: "4SERVICE GRUPPEN AS"
	   - Input: "BUNNPRIS MØRE" → Return: "BUNNPRIS"
	2. Use postnummer (postal code) to determine the appropriate regional sub-chain
	3. Match chains case-insensitively
	4. Ignore AS/ASA/BA suffixes when matching
	5. Consider partial matches if unique
	6. When in doubt, prefer the parent chain

	The input text is: "${text}"

	Based on the text, identify the most likely regionalkjede (regional chain).
	Return ONLY the name and number in the format "CHAIN_NAME (NUMBER)" or just null if no match found.`;
};
